import React, { FC, useContext } from 'react'
import { t, Trans } from '@lingui/macro'
import IconMap from '../../../../components/common/icon/IconMap'
import { Dropdown } from '../../../../components/common/dropdown/Dropdown'
import IconLayers from '../../../../components/common/icon/IconLayers'
import IconPeople from '../../../../components/common/icon/IconPeople'
import { ButtonClean } from 'front-end/src/components'
import Loader from '../../../../components/common/loader/Loader'
import IconFilter from 'public/assets/img/filter-icon.svg'
import { useAnalyticsFiltersQuery } from './AnalyticsFilter.codegen'
import './AnalyticsFilter.less'
import { AnalyticsContext } from '../../AnalyticsContext'
import { AnalyticsFilterEntityModal } from './AnalyticsFilterEntityModal'
import { SearchableItemTypesEnum } from 'shared/enums/common'
import { ButtonToggleFilterModal } from './ButtonToggleFilterModal'
import { SelectedFilters } from './SelectedFilters'
import { PickDateFilter } from '../../../../components/common/selectEntitiesForm/PickDateFilter'
import { FilterDateRangeModal } from '../../../../components/common/selectEntitiesForm/FilterDateRangeModal'
import { ChangeIncludedEntitiesCheckboxes } from '../../../../components/common/selectEntitiesForm/ChangeIncludedEntitiesCheckboxes'
import { AnalyticsFiltersEnum } from '../../constants'
import {
  EntityFilterModalControlProvider,
  ModalFilterEntitiesEnum
} from '../../../../components/common/entityFiltersModals'
import { SearchableEntity } from '../../../../components/common/selectEntitiesForm'
import { CustomEntitiesLoader } from '../../../../components/common/selectEntitiesForm/FetchEntitiesFromBackendWithCustomQuery'
import IconEnergy from '../../../../components/common/icon/IconEnergy'

interface IProps {
  activeFilters?: Array<AnalyticsFiltersEnum>
  analyticsSection?: string
  customDataLoaders?: Partial<
    Record<AnalyticsFiltersEnum, CustomEntitiesLoader>
  >
  customLoadedEntities?: Partial<
    Record<AnalyticsFiltersEnum, SearchableEntity[] | null>
  >
}

const AnalyticsFilter: FC<IProps> = ({
  activeFilters = [
    AnalyticsFiltersEnum.INCLUDE_ADMINS,
    AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
    AnalyticsFiltersEnum.DATE_FILTER,
    AnalyticsFiltersEnum.WORKSPACE_FILTER,
    AnalyticsFiltersEnum.GROUP_FILTER
  ],
  customDataLoaders,
  customLoadedEntities,
  analyticsSection
}) => {
  const { filter } = useContext(AnalyticsContext)
  const { data, loading } = useAnalyticsFiltersQuery()

  if (loading) {
    return <Loader />
  }

  const isWorkspaceSelected = () => {
    return Boolean(filter.workspaceIds)
  }

  const isPathwaySelected = () => {
    return Boolean(filter.pathwayIds)
  }

  const isGroupSelected = () => {
    return Boolean(filter.groupIds)
  }
  const isCampaignsSelected = () => {
    return !!filter.campaignIds?.length
  }

  const isWorkspaceFilterAvailable = () => {
    const showWorkspaceSelector =
      analyticsSection !== 'search-stats' && analyticsSection !== 'pathways'

    return (
      activeFilters?.includes(AnalyticsFiltersEnum.WORKSPACE_FILTER) &&
      showWorkspaceSelector
    )
  }

  const isFilterActive = (filter: AnalyticsFiltersEnum) => {
    return activeFilters?.includes(filter)
  }

  return (
    <EntityFilterModalControlProvider>
      <div className="AnalyticsFilter">
        <div className="AnalyticsFilter__wrapper">
          <label className="AnalyticsFilter__label">
            <img
              className="AnalyticsFilter__label__icon"
              src={IconFilter}
              alt={t`Filter`}
            />
            <Trans>Showing</Trans>:
          </label>
          <PickDateFilter />
          <SelectedFilters
            activeFilters={activeFilters}
            data={data}
            customLoadedEntities={customLoadedEntities}
          />

          {((isFilterActive(AnalyticsFiltersEnum.WORKSPACE_FILTER) &&
            !isWorkspaceSelected()) ||
            (isFilterActive(AnalyticsFiltersEnum.PATHWAY_FILTER) &&
              !isPathwaySelected()) ||
            (isFilterActive(AnalyticsFiltersEnum.GROUP_FILTER) &&
              !isGroupSelected()) ||
            (isFilterActive(AnalyticsFiltersEnum.CAMPAIGN_FILTER) &&
              !isCampaignsSelected())) && (
            <Dropdown
              content={
                <div className="AddFilterMenuList Dropdown">
                  <div className="DropdownLabel">
                    <Trans>Filter types</Trans>:
                  </div>
                  {isWorkspaceFilterAvailable() && (
                    <ButtonToggleFilterModal
                      Icon={IconLayers}
                      title={t`Workspaces`}
                      modalId={ModalFilterEntitiesEnum.WORKSPACE}
                      filterId={'workspaceIds'}
                    />
                  )}

                  {isFilterActive(AnalyticsFiltersEnum.PATHWAY_FILTER) && (
                    <ButtonToggleFilterModal
                      Icon={IconMap}
                      title={t`Pathways`}
                      modalId={ModalFilterEntitiesEnum.PATHWAY}
                      filterId={'pathwayIds'}
                    />
                  )}

                  {isFilterActive(AnalyticsFiltersEnum.GROUP_FILTER) && (
                    <ButtonToggleFilterModal
                      Icon={IconPeople}
                      title={t`Groups`}
                      modalId={ModalFilterEntitiesEnum.GROUP}
                      filterId={'groupIds'}
                    />
                  )}
                  {isFilterActive(AnalyticsFiltersEnum.CAMPAIGN_FILTER) && (
                    <ButtonToggleFilterModal
                      Icon={IconEnergy}
                      title={t`Campaign`}
                      modalId={ModalFilterEntitiesEnum.CAMPAIGN}
                      filterId={'campaignIds'}
                    />
                  )}
                </div>
              }
            >
              <ButtonClean className="AnalyticsFilter__add">
                + <Trans>Add filter</Trans>
              </ButtonClean>
            </Dropdown>
          )}
        </div>

        <ChangeIncludedEntitiesCheckboxes activeFilters={activeFilters} />

        {isFilterActive(AnalyticsFiltersEnum.DATE_FILTER) && (
          <FilterDateRangeModal />
        )}
        {data && (
          <>
            {isFilterActive(AnalyticsFiltersEnum.WORKSPACE_FILTER) && (
              <AnalyticsFilterEntityModal
                filterKey="workspaceIds"
                entities={data.workspacesForAnalytics}
                modalId={ModalFilterEntitiesEnum.WORKSPACE}
                entityType={SearchableItemTypesEnum.workspaces}
                header={t`Workspaces`}
                subHeader={t`Filter to include only activity within one or more workspaces:`}
              />
            )}
            {isFilterActive(AnalyticsFiltersEnum.CAMPAIGN_FILTER) && (
              <AnalyticsFilterEntityModal
                filterKey="campaignIds"
                entities={
                  customLoadedEntities?.[AnalyticsFiltersEnum.CAMPAIGN_FILTER]
                }
                loadEntities={
                  customDataLoaders?.[AnalyticsFiltersEnum.CAMPAIGN_FILTER]
                }
                modalId={ModalFilterEntitiesEnum.CAMPAIGN}
                entityType={SearchableItemTypesEnum.campaigns}
                header={t`Campaigns`}
                subHeader={t`Filter to include only activity within one or more campaigns:`}
              />
            )}

            {isFilterActive(AnalyticsFiltersEnum.PATHWAY_FILTER) && (
              <AnalyticsFilterEntityModal
                filterKey="pathwayIds"
                entities={data.pathways.items}
                modalId={ModalFilterEntitiesEnum.PATHWAY}
                entityType={SearchableItemTypesEnum.pathways}
                header={t`Pathways`}
                subHeader={t`Filter to include only activity within one or more pathways:`}
              />
            )}
            {isFilterActive(AnalyticsFiltersEnum.GROUP_FILTER) && (
              <AnalyticsFilterEntityModal
                filterKey="groupIds"
                entities={data?.groups}
                modalId={ModalFilterEntitiesEnum.GROUP}
                entityType={'groups'}
                header={t`Groups`}
                subHeader={t`Filter to include only activity by members of 1 or more groups:`}
              />
            )}
          </>
        )}
      </div>
    </EntityFilterModalControlProvider>
  )
}

export default AnalyticsFilter
